import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { CREDENTIAL_DEFAULTS, mergeCredentials } from '../../data/credentialDefaults';
import { BadgeCheck, Save, Loader2 } from 'lucide-react';

const CredentialsManager = () => {
  const [items, setItems] = useState(CREDENTIAL_DEFAULTS);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi.get('/credentials');
        setItems(mergeCredentials(res.data?.items));
      } catch (e) {
        toast({ title: 'Load failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      }
    })();
  }, []);

  const setField = (key, field, value) => {
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, [field]: value } : it)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.post('/credentials', { id: 'credentials', items });
      toast({ title: 'Saved', description: 'Trust badge text updated on the website.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="credentials-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 max-w-2xl">
          These four trust badges appear on the homepage About section (Registered NGO, Tax-Exempt,
          Child-Safe Policy, Annual Audits). Edit the card text and the popup text shown when visitors click a badge.
        </p>
        <Button onClick={save} disabled={saving} className="bg-[#1565C0] hover:bg-[#0D47A1]" data-testid="credentials-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save All
        </Button>
      </div>

      {items.map((it) => (
        <Card key={it.key} data-testid={`credential-card-${it.key}`}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <BadgeCheck className="w-5 h-5 text-[#1565C0]" /> {it.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Card title</Label>
              <Input value={it.title} onChange={(e) => setField(it.key, 'title', e.target.value)}
                data-testid={`cred-${it.key}-title`} className="mt-1" />
            </div>
            <div>
              <Label>Card subtitle</Label>
              <Input value={it.subtitle} onChange={(e) => setField(it.key, 'subtitle', e.target.value)}
                data-testid={`cred-${it.key}-subtitle`} className="mt-1" />
            </div>
            <div>
              <Label>Popup title</Label>
              <Input value={it.modal_title} onChange={(e) => setField(it.key, 'modal_title', e.target.value)}
                data-testid={`cred-${it.key}-modal-title`} className="mt-1" />
            </div>
            <div>
              <Label>Popup subtitle</Label>
              <Input value={it.modal_subtitle} onChange={(e) => setField(it.key, 'modal_subtitle', e.target.value)}
                data-testid={`cred-${it.key}-modal-subtitle`} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>Popup text (blank line = new paragraph)</Label>
              <Textarea rows={4} value={it.body} onChange={(e) => setField(it.key, 'body', e.target.value)}
                data-testid={`cred-${it.key}-body`} className="mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>Bullet points (one per line, optional)</Label>
              <Textarea rows={3} value={(it.bullets || []).join('\n')}
                onChange={(e) => setField(it.key, 'bullets', e.target.value.split('\n').filter((l) => l.trim()))}
                data-testid={`cred-${it.key}-bullets`} className="mt-1" />
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#1565C0] hover:bg-[#0D47A1]" data-testid="credentials-save-btn-bottom">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save All
        </Button>
      </div>
    </div>
  );
};

export default CredentialsManager;
